// My Teams (v3 teardown).
//
// Every starred team + their next 7 days of fixtures (live / upcoming /
// recent settled). Flat hairline rows under v3 chrome.

import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Star, Calendar, Activity, Trophy } from "lucide-react";
import { API } from "../App";
import { useAuth } from "../context/AuthContext";
import TopNav from "../components/TopNav";
import FollowTeamButton from "../components/FollowTeamButton";
import {
  V3Page, V3Container, V3Header, V3Eyebrow, V3Section,
  V3Tag, V3Num, V3Button,
} from "../components/v3";

const formatKickoff = (iso) => {
  if (!iso) return "TBD";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      weekday: "short", day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso?.slice(0, 16) || "TBD";
  }
};

const FixtureRow = ({ f, onClick, kind }) => {
  const isSettled = kind === "settled";
  const isLive = kind === "live";
  return (
    <li
      onClick={() => f?.fixture_id && onClick(f.fixture_id)}
      className="px-3 py-2.5 cursor-pointer transition-colors"
      style={{
        borderTop: "1px solid var(--v3-line)",
        background: "var(--v3-surface)",
      }}
      data-testid={`my-teams-fixture-${f.fixture_id}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="text-[13px] leading-tight truncate" style={{ fontWeight: 500 }}>
          {f.home_team?.name}
          <span className="mx-1" style={{ color: "var(--v3-text-4)" }}>vs</span>
          {f.away_team?.name}
        </div>
        {isSettled ? (
          <V3Num className="text-[14px] shrink-0">{f.goals_home}–{f.goals_away}</V3Num>
        ) : isLive ? (
          <V3Tag tone="rose">
            <span
              className="w-1.5 h-1.5 rounded-full animate-pulse"
              style={{ background: "var(--v3-rose, #f43f5e)" }}
            />
            {f.status}
          </V3Tag>
        ) : (
          <span
            className="text-[10px] uppercase shrink-0"
            style={{
              color: "var(--v3-text-3)",
              fontFamily: "var(--v3-font-mono)",
              letterSpacing: "0.12em",
            }}
          >
            {formatKickoff(f.date)}
          </span>
        )}
      </div>
      <div
        className="text-[11px] mt-1"
        style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)" }}
      >
        {f.league?.name} · {f.league?.country}
      </div>
    </li>
  );
};

const MyTeamsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    axios
      .get(`${API}/me/teams-fixtures?days=7`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000,
      })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <V3Page testid="my-teams-page">
      <TopNav />
      <V3Container>
        <V3Header
          testid="my-teams-header"
          eyebrow={<V3Eyebrow icon={Star}>Starred teams</V3Eyebrow>}
          title="My teams."
          titleAccent="Next 7 days, every starred side."
          subtitle="Every team you've starred plus their live, upcoming and recent fixtures. Tap any fixture to dive into the model's pre-match breakdown."
        />

        {loading ? (
          <div
            className="py-16 text-[13px]"
            style={{ color: "var(--v3-text-3)", fontFamily: "var(--v3-font-mono)" }}
          >
            Loading starred teams…
          </div>
        ) : !data || !data.teams?.length ? (
          <V3Section testid="my-teams-empty">
            <div
              className="p-10 text-center"
              style={{ border: "1px solid var(--v3-line)", background: "var(--v3-surface)" }}
            >
              <Star className="w-8 h-8 mx-auto mb-3" style={{ color: "var(--v3-text-4)" }} />
              <p className="text-base" style={{ fontWeight: 500 }}>No teams starred yet</p>
              <p
                className="mt-2 text-[13px] max-w-md mx-auto"
                style={{ color: "var(--v3-text-3)" }}
              >
                Tap the ⭐ next to any team name on the homepage, Now Playing
                cards, or match details page. They'll show up here with their
                fixture calendar and live alerts.
              </p>
              <V3Button
                className="mt-6"
                onClick={() => navigate("/")}
                data-testid="my-teams-cta-home"
              >
                Go to homepage
              </V3Button>
            </div>
          </V3Section>
        ) : (
          <V3Section label="Starred teams" testid="my-teams-list">
            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-0"
              style={{ borderTop: "1px solid var(--v3-line)" }}
              data-testid="my-teams-grid"
            >
              {data.teams.map((t, i) => (
                <div
                  key={t.team_id}
                  className="p-5"
                  style={{
                    borderBottom: "1px solid var(--v3-line)",
                    borderRight: i % 2 === 0 ? "1px solid var(--v3-line)" : "none",
                    background: "var(--v3-surface)",
                  }}
                  data-testid={`my-teams-team-${t.team_id}`}
                >
                  <div className="flex items-center justify-between gap-3 mb-5">
                    <div className="inline-flex items-center gap-2">
                      <span className="text-[15px]" style={{ fontWeight: 500, letterSpacing: "-0.015em" }}>
                        {t.team_name}
                      </span>
                      <FollowTeamButton teamId={t.team_id} teamName={t.team_name} size="md" />
                    </div>
                    <span
                      className="text-[10px] uppercase shrink-0"
                      style={{
                        color: "var(--v3-text-3)",
                        fontFamily: "var(--v3-font-mono)",
                        letterSpacing: "0.14em",
                      }}
                    >
                      {t.fixtures_total} fixture{t.fixtures_total === 1 ? "" : "s"}
                    </span>
                  </div>

                  {t.live.length > 0 && (
                    <GroupBlock icon={Activity} label="Live now" tone="rose">
                      <ul>
                        {t.live.map((f) => (
                          <FixtureRow key={f.fixture_id} f={f} kind="live" onClick={(id) => navigate(`/match/${id}`)} />
                        ))}
                      </ul>
                    </GroupBlock>
                  )}

                  {t.upcoming.length > 0 && (
                    <GroupBlock icon={Calendar} label="Next 7 days">
                      <ul>
                        {t.upcoming.slice(0, 5).map((f) => (
                          <FixtureRow key={f.fixture_id} f={f} kind="upcoming" onClick={(id) => navigate(`/match/${id}`)} />
                        ))}
                      </ul>
                    </GroupBlock>
                  )}

                  {t.recent_settled.length > 0 && (
                    <GroupBlock icon={Trophy} label="Last results">
                      <ul>
                        {t.recent_settled.slice(0, 3).map((f) => (
                          <FixtureRow key={f.fixture_id} f={f} kind="settled" onClick={(id) => navigate(`/match/${id}`)} />
                        ))}
                      </ul>
                    </GroupBlock>
                  )}

                  {t.fixtures_total === 0 && (
                    <p className="text-[13px]" style={{ color: "var(--v3-text-3)" }}>
                      No fixtures in the next 7 days.
                    </p>
                  )}
                </div>
              ))}
            </div>
          </V3Section>
        )}
      </V3Container>
    </V3Page>
  );
};

function GroupBlock({ icon: Icon, label, tone, children }) {
  const toneColor =
    tone === "rose" ? "var(--v3-rose, #fda4af)" : "var(--v3-text-3)";
  return (
    <div className="mb-4 last:mb-0">
      <div
        className="inline-flex items-center gap-1.5 mb-1 text-[10px] uppercase"
        style={{
          color: toneColor,
          fontFamily: "var(--v3-font-mono)",
          letterSpacing: "0.14em",
          fontWeight: 500,
        }}
      >
        <Icon className="w-3 h-3" strokeWidth={1.75} />
        {label}
      </div>
      {children}
    </div>
  );
}

export default MyTeamsPage;
